<template>
  <div class="d-flex flex-column" style="row-gap: 7px">
    <!-- Check if product exists before rendering the image -->
    <!-- region Product Image -->
    <div v-if="product && product.image1" class="product-image">
      <img :src="product.image1" :alt="product.name" class="imageCoversParent"/>
    </div>
    <!-- endregion -->

    <!-- region Long Description -->
    <Accordion v-if="product && product.longDesc" :title="$t('subTitle.productDetails')">
      <!-- Check if longDesc exists -->
      <div class="text-left" style="white-space: pre-line">{{ product.longDesc }}</div>
    </Accordion>
    <!-- endregion -->

    <!-- region Product Properties -->
    <Accordion v-if="product && product.properties && product.properties.length > 0" :title="$t('subTitle.properties')">
      <div class="small-grid-layout">
        <div class="filter-item" v-for="property in product.properties" :key="property.id">
          <div class="filter-icon">
            <img :src="property.image" :alt="property.name" class="imageFitsParent" />
          </div>
          <span>{{ property.name }}</span>
        </div>
      </div>
    </Accordion>
    <!-- endregion -->

    <!-- region Allergens -->
    <Accordion v-if="product && product.allergens && product.allergens.length > 0" :title="$t('subTitle.allergens')">
      <div class="small-grid-layout">
        <div class="filter-item" v-for="allergen in product.allergens" :key="allergen.id">
          <div class="filter-icon">
            <img :src="allergen.image" :alt="allergen.name" class="imageFitsParent" />
          </div>
          <span>{{ allergen.name }}</span>
        </div>
      </div>
    </Accordion>
    <!-- endregion -->

    <!-- region Nutrients -->
    <Accordion v-if="product && product.nutriValues && product.nutriValues.length > 0" :title="$t('subTitle.nutriValues')">
      <div class="allergens" v-for="nutriValue in product.nutriValues" :key="nutriValue.id">
        <span class="text-left">
          {{ nutriValue.name }}
        </span>
        <span class="text-right">
          {{ nutriValue.value }}
        </span>
      </div>
    </Accordion>

    <hr class="noMargin">

    <div v-if="maxQuantity > 0">
      <StructureEditGroup
        id="editItem"
        ref="editItem2"
        v-for="groupNode in getStructure()"
        :key="groupNode.lineId"
        :structure="groupNode"
        v-model="productOptions"
        :structure-settings="structureSettings"
      />
    </div>

    <div v-if="maxQuantity > 0">
      <b-form-spinbutton
        v-model="quantity"
        :max="maxQuantity"
        :min="1"
      />
    </div>

    <div>
      <Button v-if="quantity > 0 && maxQuantity > 0" icon-left="cart-plus" class="btn btn-accordion" @click="handleProductUpdateWithOptions" :disabled="!isValid" >
        <span>{{ formattedTotalPrice }}</span>
      </Button>
      <Button icon-left="close" class="btn btn-accordion" secondary @click="closeModal">
        <span>{{ $t('button.cancel') }}</span>
      </Button>
    </div>
  </div>
</template>

<script>
import Utils from "@/utils/utils";
import {productService} from "@/services/api/productService";
import Accordion from "@/components/layout-components/Accordion.vue";
import StructureEditGroup from "@/components/layout-components/StructureEditGroup.vue";
import {StructureUtils} from "@/utils/structureUtils";
import Button from "@/components/barrier-free-template-components/Button.vue";
export default {
  props: {
    productId: {
      type: Number,
      required: true,
    },
    maxQuantity: { // remove
      type: Number,
      required: false,
    },
    groupId: { // remove
      type: Number,
      required: false,
    },
  },
  data() {
    return {
      /** @type {ProductDetails.product|{}} */
      product: {},
      quantity: 1,
      productOptions: [],
      /** @type ?number */
      totalPrice: undefined,
      /**
       * @volatile
       * @type Array<string> */
      valid: true,
      /** @type StructureEditSettings */
      structureSettings: {
        idPropGroup: 'lineId',
        idPropNode: 'kkProdId',
        namePropGroup: 'name',
        namePropNode: 'name',
        hasChildPropNode: 'hasChildren',
        childPropNode: 'items',
        childPropGroup: 'items',
      },
    };
  },
  async created() {
    if (!this.productId) return;

    this.loading = true;
    const result = await productService.getProductDetails(this.productId, true, true, true, true, true);
    this.loading = false;

    if (result.exception || !result.product) {
      this.makeToast("warning", this.$t("notification.getItemFailed"));
      return;
    }

    this.product = result.product;
    this.updateTotalPrice();
  },
  updated(){
    this.updateValidity();
  },
  components: {
    Button,
    StructureEditGroup,
    Accordion,
  },
  computed: {
    formattedTotalPrice() {
      return Utils.formatCurrency(this.quantity * (this.totalPrice || this.product.price || 0) , window.config.ACCOUNT_BALANCE_CURRENCY)
    },
    isValid(){
      return this.valid;
    },
  },
  methods: {
    updateTotalPrice() {
      let basePrice = this.product.price || 0;

      let additionalPrice = StructureUtils.getActiveNodes(this.productOptions, this.getStructure(), this.structureSettings).reduce((total, option) => {
        return total + (option.node.price || 0); // Using collected price directly
      }, 0)

      this.totalPrice = this.quantity * (basePrice + additionalPrice);
    },
    handleProductUpdateWithOptions() {
      this.$emit(
        "product-update",
        this.product[this.structureSettings.idPropNode],
        this.quantity,
        this.totalPrice || this.product.price || 0,
        this.resolveItemList(this.productOptions),
        this.groupId
      );

      this.closeModal();
    },
    closeModal() {
      this.$emit('close');
    },
    /** @return {Array<ProductDetailGroupNode>} */
    getStructure(){
      if (!this.product[this.structureSettings.hasChildPropNode]) // isUniqueItem()
        return [];

      return this.product[this.structureSettings.childPropGroup];
    },
    resolveItemList(pathSet) {
      return StructureUtils.convertToServer(
        pathSet,
        this.getStructure(),
        this.structureSettings,
        "groupId",
        "kkProdId"
      );
    },
    unresolveItemList(compiledItems) {
      return StructureUtils.convertToClient(
        compiledItems,
        this.getStructure(),
        this.structureSettings,
        "groupId",
        "kkProdId"
      );
    },
    updateValidity(){
      if (!this.$refs.editItem2) {
        this.valid = true;
      } else {
        this.valid = this.$refs.editItem2.reduce((p,g)=>p && g.getValid(),true)
      }
    },
  },
  watch: {
    'productOptions': function (){
      this.updateTotalPrice();
      this.updateValidity();
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/css/customizer';

.modal-body .product-image {
  aspect-ratio: 1 / 1;
  /* todo - Customize this icon */
}

.product-details {
  display: flex;
}

.filter-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
}

.filter-icon {
  height: 60px;
  width: 60px;
}

@media (max-width: 768px) {
  .filter-icon {
    height: 50px;
    width: 50px;
  }
}

.allergens {
  display: flex;
  gap: 10px;
  justify-content: space-between;
}

.allergen-img {
  max-width: 30px;
  margin-right: 5px;
}

.small-header {
  font-size: 0.9em; /* Small header font size */
  text-align: left; /* Align header text to the left */
  background: none;
  padding: 0;
}

.btn-accordion {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 16px;
  width: 100%;
  margin: 5px 0 0;
}

.addIconMenu {
  margin-left: 5px;
}

/* Align radio buttons and checkboxes to the left */
.b-form-radio-group,
.b-form-checkbox-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.b-form-radio,
.b-form-checkbox {
  margin-left: 0; /* Align radio buttons and checkboxes to the left */
}

/* Add padding to b-card-header title */
.b-card-header {
  padding-left: 1rem; /* Adjust as needed to match collapse button padding */
}

/* Override SpinButton styling */
.custom-spin-button {
  display: flex;
  align-items: stretch;
  width: auto;
  background-color: transparent;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 0;
  justify-content: space-between;
}

/* Override button styles */
.custom-spin-button button {
  background-color: transparent;
  color: #000;
  border: none;
  padding: 5px;
  font-size: 14px;
  cursor: pointer;
}

.custom-spin-button button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* Override input field styles */
.custom-spin-button input {
  border: none;
  text-align: center;
  width: 50px;
  font-size: 14px;
  padding: 5px;
  background-color: #f9f9f9;
}
</style>






